<template>
  <div class="d-flex flex-column-fluid financing-requests-page">
    <div class="container">
      <!-- Page Header -->
      <div class="page-header mb-6">
        <div class="header-content">
          <div class="header-title">
            <h1 class="title" v-if="businessId">Financing Invoices for Businesss</h1>
            <h1 class="title" v-else>All Financing Invoices</h1>
            <p class="subtitle" v-if="businessId">Track and manage finance invoices for this business</p>
            <p class="subtitle" v-else>Track and manage all finance invoices</p>
          </div>
          <div class="header-actions">
            <router-link 
              v-if="businessId"
              :to="{ name: 'business-insights', params: { business_id: businessId } }" 
              class="btn btn-dark btn-dashboard"
            >
              <i class="flaticon2-line-chart"></i>
              Back to Business Dashboard
            </router-link>
            <router-link 
              v-else
              :to="{ name: 'financing-analytics' }" 
              class="btn btn-dark btn-dashboard"
            >
              <i class="flaticon2-line-chart"></i>
              Back to Analytics Dashboard
            </router-link>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="spinner-border" role="status"></div>
        <div class="loading-text">Loading requests...</div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-state">
        <div class="alert alert-danger">
          <i class="material-icons">error</i>
          {{ error }}
        </div>
      </div>

      <!-- Main Content -->
      <div v-else>


        <!-- Filters -->
        <div class="filters-section">
          <h5 class="filters-title">Filter Invoices</h5>
          <div class="row align-items-end">
            <div class="col-md-3 filter-group">
              <label class="form-label">Invoice Reference or Business Name</label>
              <input 
                type="text" 
                class="form-control" 
                placeholder="Search by reference..." 
                v-model="filters.invoice_reference"
                @input="debouncedSearch"
              />
            </div>
            <div class="col-md-3 filter-group">
              <label class="form-label">Status</label>
              <select class="form-control" v-model="filters.status" @change="applyFilters">
                <option value="">All Statuses</option>
                <option value="PENDING_REVIEW">Pending Review</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
                <option value="DISBURSED_AMOUNT">Disbursed Amount</option>
                <option value="PARTIALLY_PAID">Partially Paid</option>
              </select>
            </div>
            <div class="col-md-3 filter-group">
              <label class="form-label">Business</label>
              <div class="business-selector position-relative">
                <div class="search-wrapper">
                  <input
                    ref="businessSearchInput"
                    type="text"
                    class="form-control search-input"
                    v-model="businessSearchTerm"
                    @input="searchBusinesses"
                    @focus="onSearchFocus"
                    @blur="onSearchBlur"
                    :placeholder="selectedBusinesses.length > 0 ? `${selectedBusinesses.length} business(es) selected` : 'Type to search businesses...'"
                  />
                  <i class="las la-search search-icon"></i>
                </div>
                <div 
                  v-if="showBusinessDropdown" 
                  class="business-dropdown"
                  ref="businessDropdown"
                  @mouseenter="onDropdownMouseEnter"
                  @mouseleave="onDropdownMouseLeave"
                  :style="{ minWidth: '100%', left: 0, right: 'auto' }"
                >
                  <div v-if="loadingBusinesses && filteredBusinesses.length === 0" class="dropdown-loading">
                    <div class="spinner-border spinner-border-sm mr-2"></div>
                    Loading businesses...
                  </div>
                  <div v-else-if="filteredBusinesses.length === 0 && !loadingBusinesses" class="dropdown-empty">
                    No businesses found
                  </div>
                  <div v-else class="business-list" @scroll="onDropdownScroll" ref="businessList">
                    <div 
                      v-for="business in filteredBusinesses" 
                      :key="business.id"
                      class="business-item"
                      :class="{ 'selected': selectedBusinesses.some(b => b.id === business.id) }"
                      @click.stop="toggleBusinessSelection(business)"
                    >
                      <span class="business-name">{{ business.name }}</span>
                      <span class="business-action">
                        <i v-if="selectedBusinesses.some(b => b.id === business.id)" class="las la-check text-success" title="Selected"></i>
                        <i v-else class="las la-plus text-primary" title="Select"></i>
                      </span>
                    </div>
                    <div v-if="isLoadingMore" class="dropdown-loading-more">
                      <div class="spinner-border spinner-border-sm mr-2"></div>
                      Loading more businesses...
                    </div>
                  </div>
                </div>
                <div v-if="selectedBusinesses.length > 0" class="selected-business-display mt-2">
                  <div class="selected-business-chips d-flex flex-wrap gap-2">
                    <span v-for="business in selectedBusinesses" :key="business.id" class="business-chip d-inline-flex align-items-center">
                      <span class="business-name mr-2">{{ business.name }}</span>
                      <button 
                        type="button" 
                        class="remove-btn btn btn-link p-0 m-0"
                        @click="removeBusinessSelection(business)"
                        title="Remove selection"
                      >
                        <i class="las la-times"></i>
                      </button>
                    </span>
                    <button 
                      type="button" 
                      class="clear-all-btn btn btn-link text-danger p-0 ml-2"
                      @click="clearAllBusinessSelections"
                      title="Clear all selections"
                    >
                      <i class="las la-times-circle"></i>
                      Clear All
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-3 filter-group d-flex align-items-end">
              <button class="btn btn-dark w-100" @click="clearFilters">
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div
          class="empty-state text-center"
          v-if="isEmpty && !loadingRequests"
        >
          <i class="las la-file-invoice la-5x text-muted mb-3"></i>
          <div class="empty-title">
            <span v-if="hasActiveFilters">No requests found for your filters</span>
            <span v-else>No financing requests found</span>
          </div>
          <div class="empty-subtitle" v-if="hasActiveFilters">
            Try adjusting your search terms or filters
          </div>
        </div>

        <!-- Error State -->
        <div
          class="empty-state text-center"
          v-if="error"
        >
          <i class="las la-exclamation-triangle la-5x text-danger mb-3"></i>
          <div class="empty-title text-danger">
            {{ error }}
          </div>
          <button 
            class="btn btn-primary btn-sm mt-3" 
            @click="loadRequests"
          >
            <i class="las la-redo mr-1"></i>
            Try Again
          </button>
        </div>

        <!-- Requests Table -->
        <financing-table
          v-if="!isEmpty && !error"
          :columns="tableColumns"
          :data="requests"
          :grid-template="'180px 160px 200px 150px 180px 190px 100px'"
          :current-page="currentPage"
          :total-rows="totalRequests"
          :page-size="pageSize"
          :table-name="'financing-requests'"
          :empty-title="hasActiveFilters ? 'No requests found for your filters' : 'No financing requests found'"
          :empty-subtitle="hasActiveFilters ? 'Try adjusting your search terms or filters' : ''"
          :empty-icon="'las la-file-invoice'"
          :on-row-click="viewRequest"
          @page-change="setPage"
          @action="handleTableAction"
        />

        <!-- Pagination -->
        <div class="pagination-wrapper" v-if="totalPages > 1">
          <base-pagination
            :activePage="currentPage"
            :totalRows="totalRequests"
            :pageSize="pageSize"
            @set-page="setPage"
          />
        </div>
      </div>
    </div>
    <!-- Finance Request Modal -->
    <finance-request-modal 
      ref="financeRequestModal" 
      :currency="currency"
      @updated="onRequestUpdated"
    />
    
    <!-- PDF Viewer -->
    <pdf-viewer 
      v-if="showPDF" 
      :url="pdfUrl" 
      :name="pdfName"
      :showHeader="true"
      :forceExpansion="true"
      :enableOverlayEffect="true"
      @closePDF="closePDFViewer"
    />
  </div>
</template>

<script>
import FinancingService from "@/services/financing.service";
import CompaniesService from "@/services/companies.service";
import BasePagination from "@/components/UI/BasePagination.vue";
import FinancingTable from "@/components/UI/FinancingTable.vue";
import FinanceRequestModal from "./components/FinanceRequestModal.vue";
import PdfViewer from "@/components/pdfViewer.vue";
import { formatErrorMessage } from "@/utils/random_utils";

export default {
  name: "FinancingRequests",
  components: {
    BasePagination,
    FinancingTable,
    FinanceRequestModal,
    PdfViewer
  },
  data() {
    return {
      loading: true,
      loadingRequests: false,
      error: null,
      
      // Requests data
      requests: [],
      totalRequests: 0,
      currentPage: 1,
      pageSize: 20,
      currency: 'AED',
      
      // Filters
      filters: {
        invoice_reference: '',
        status: ''
      },
      searchDebounce: null,
      
      // Business selection
      selectedBusinesses: [],
      businessSearchTerm: "",
      showBusinessDropdown: false,
      loadingBusinesses: false,
      businessSearchDebounce: null,
      scrollDebounce: null,
      businessPage: 1,
      hasMoreBusinesses: false,
      totalBusinesses: 0,
      isInteractingWithDropdown: false,
      isLoadingMore: false,
      businessId: null,
      availableBusinesses: [],
      
      // PDF Viewer
      showPDF: false,
      pdfUrl: null,
      pdfName: '',
    };
  },
  
  computed: {
    totalPages() {
      return this.totalRequests / this.pageSize;
    },
    
    hasActiveFilters() {
      return this.filters.invoice_reference || this.filters.status || this.selectedBusinesses.length > 0;
    },
    
    isEmpty() {
      return this.requests.length === 0;
    },
    
    filteredBusinesses() {
      return this.availableBusinesses;
    },
    
    tableColumns() {
      return [
        {
          header: 'Company',
          field: 'borrower_name',
          type: 'text',
          align: 'text-center',
          class: 'business-name-col',
          icon: 'las la-building'
        },
        {
          header: 'Invoice Number',
          field: 'invoice_reference',
          type: 'text',
          align: 'text-center',
          class: 'reference-col',
          icon: 'las la-file-invoice'
        },
        {
          header: 'Total Purchased Receivable Amount',
          field: 'total_receivable_amount',
          type: 'currency',
          align: 'text-right',
          class: 'amount-col',
          icon: 'las la-money-bill',
          defaultCurrency: 'AED'
        },
        {
          header: 'Total Fees',
          field: 'total_fees',
          type: 'currency',
          align: 'text-right',
          class: 'transaction-fee-col',
          icon: 'las la-percentage',
          defaultCurrency: 'AED'
        },
        {
          header: 'Total Disbursed Amount',
          field: 'disbursement_amount',
          type: 'currency',
          align: 'text-right',
          class: 'disbursement-col',
          icon: 'las la-credit-card',
          defaultCurrency: 'AED'
        },
        {
          header: 'Status',
          field: 'status',
          type: 'status',
          align: 'text-center',
          class: 'status-col',
          icon: 'las la-toggle-on'
        },
        {
          header: 'Invoice',
          field: 'file',
          type: 'action',
          action: 'download',
          align: 'text-center',
          class: 'invoice-col',
          icon: 'las la-file-pdf',
          tooltip: 'View Invoice PDF'
        }
      ];
    }
  },
  
  async mounted() {
    this.businessId = this.$route.params.business_id || this.$route.query.business_id;
    document.addEventListener('click', this.handleClickOutside);
    document.addEventListener('keydown', this.handleKeydown);
    // Get filters from query params if any
    this.initializeFromQuery();
    await Promise.all([
      this.loadRequests(),
      this.loadInitialBusinesses()
    ]);
  },
  
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
    document.removeEventListener('keydown', this.handleKeydown);
    clearTimeout(this.businessSearchDebounce);
    clearTimeout(this.scrollDebounce);
  },
  
  methods: {
    initializeFromQuery() {
      if (this.$route.query.invoice_reference) {
        this.filters.invoice_reference = this.$route.query.invoice_reference;
      }
      if (this.$route.query.status) {
        this.filters.status = this.$route.query.status;
      }
      if (this.$route.query.page) {
        this.currentPage = parseInt(this.$route.query.page);
      }
      if (this.$route.query.page_size) {
        this.pageSize = parseInt(this.$route.query.page_size);
      }
      if (this.$route.query.business_id) {
        this.business_id = this.$route.query.business_id;
      }
    },
    
    async loadRequests() {
      try {
        this.loadingRequests = true;
        this.error = null;
        
        const params = {
          page: this.currentPage,
          page_size: this.pageSize
        };
        
        if (this.filters.invoice_reference) {
          params.invoice_reference = this.filters.invoice_reference;
        }
        if (this.filters.status) {
          params.status = this.filters.status;
        }
        if (this.selectedBusinesses.length > 0) {
          params.borrower_ids = this.selectedBusinesses.map(b => b.id).join(',');
        }
        
        if (this.businessId) {
          params.business_id = this.businessId;
        }
        
        // Update URL with current filters
        this.updateURL(params);
        
        const response = await FinancingService.getAdminRequests(params);
        
        if (response && response.data) {
          this.requests = response.data || [];
          this.totalRequests = response.count || 0;
          
          // Get currency from first request or default to AED
          if (this.requests.length > 0) {
            this.currency = response.currency || 'AED';
          }
        }
      } catch (error) {
        console.error('Error loading requests:', error);
        this.error = formatErrorMessage(error.message || 'Failed to load requests');
        this.requests = [];
      } finally {
        this.loadingRequests = false;
        this.loading = false;
      }
    },
    
    updateURL(params) {
      const query = {};
      if (this.filters.invoice_reference) query.invoice_reference = this.filters.invoice_reference;
      if (this.filters.status) query.status = this.filters.status;
      if (params.page > 1) query.page = params.page;
      if (params.page_size !== 25) query.page_size = params.page_size;
      if (this.business_id) query.business_id = this.business_id;
      
      this.$router.replace({ query });
    },
    
    async setPage(page, pageSize) {
      // If pageSize is provided and different from current, update it
      if (pageSize && pageSize !== this.pageSize) {
        this.pageSize = pageSize;
      }
      
      // Fetch requests for the requested page
      if (page >= 1) {
        this.currentPage = page;
        await this.loadRequests();
      }
    },
    
    async changePageSize() {
      this.currentPage = 1;
      await this.loadRequests();
    },
    
    debouncedSearch() {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => {
        this.applyFilters();
      }, 500);
    },
    
    async applyFilters() {
      this.currentPage = 1;
      await this.loadRequests();
    },
    
    clearFilters() {
      this.filters = {
        invoice_reference: '',
        status: ''
      };
      this.selectedBusinesses = []; // Clear selected businesses
      this.applyFilters();
    },
    
    viewRequest(request) {
      // Open the finance request modal with business ID if available
      const businessId = request.borrower_id || request.business_id;
      this.$refs.financeRequestModal.open(request.id, businessId);
    },
    
    editRequest(request) {
      // TODO: Implement edit request functionality
    },
    
    formatCurrency(amount) {
      if (!amount || isNaN(amount)) return this.$formatCurrency(0, this.currency);
      return this.$formatCurrency(amount, this.currency);
    },
    
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    
    formatStatus(status) {
      const statusMap = {
        'PENDING_REVIEW': 'Pending Review',
        'APPROVED': 'Approved',
        'REJECTED': 'Rejected',
        'DISBURSED_AMOUNT': 'Disbursed Amount',
        'PARTIALLY_PAID': 'Partially Paid'
      };
      return statusMap[status] || status;
    },
    
    getStatusClass(status) {
      const classMap = {
        'PENDING_REVIEW': 'status-pending-review',
        'APPROVED': 'status-approved',
        'REJECTED': 'status-rejected',
        'DISBURSED_AMOUNT': 'status-disbursed',
        'PARTIALLY_PAID': 'status-partially-paid'
      };
      return classMap[status] || 'status-default';
    },
    
    // Business selection methods
    async loadInitialBusinesses() {
      try {
        const response = await CompaniesService.query({
          page: 1,
          page_size: 20
        });

        const businesses = response.data || [];
        
        this.availableBusinesses = businesses.map(business => ({
          id: business.id,
          name: business.name || `Business ${business.id}`
        }));

        this.totalBusinesses = response.count || 0;
        this.hasMoreBusinesses = !!response.next;
        this.businessPage = 1;

      } catch (err) {
        console.error('Failed to load initial businesses:', err);
        this.availableBusinesses = [];
        this.hasMoreBusinesses = false;
      }
    },

    async loadBusinesses(reset = false) {
      if (reset) {
        this.businessPage = 1;
        this.availableBusinesses = [];
        this.hasMoreBusinesses = false;
      }

      this.loadingBusinesses = true;
      try {
        const params = {
          page: this.businessPage,
          page_size: 20,
        };

        if (this.businessSearchTerm) {
          params.search = this.businessSearchTerm;
        }

        const response = await CompaniesService.query(params);
        
        const businesses = response.data || [];
        
        const formattedBusinesses = businesses.map(business => ({
          id: business.id,
          name: business.name || `Business ${business.id}`
        }));
        
        if (reset) {
          this.availableBusinesses = formattedBusinesses;
        } else {
          this.availableBusinesses = [...this.availableBusinesses, ...formattedBusinesses];
        }

        this.totalBusinesses = response.count || 0;
        this.hasMoreBusinesses = !!response.next;

      } catch (err) {
        console.error('Failed to load businesses:', err);
        if (reset) {
          this.availableBusinesses = [];
        }
        this.hasMoreBusinesses = false;
      } finally {
        this.loadingBusinesses = false;
      }
    },

    async loadMoreBusinesses() {
      if (this.hasMoreBusinesses && !this.loadingBusinesses && !this.isLoadingMore) {
        this.isLoadingMore = true;
        this.businessPage += 1;
        await this.loadBusinesses(false);
        this.isLoadingMore = false;
      }
    },

    async searchBusinesses() {
      if (this.businessSearchDebounce) clearTimeout(this.businessSearchDebounce);
      
      if (this.businessSearchTerm.length >= 2) {
        this.showBusinessDropdown = true;
        this.businessSearchDebounce = setTimeout(() => this.loadBusinesses(true), 300);
      } else if (this.businessSearchTerm.length === 0) {
        this.showBusinessDropdown = true;
        this.loadBusinesses(true);
      } else if (this.businessSearchTerm.length === 1) {
        this.showBusinessDropdown = true;
        this.loadBusinesses(true);
      } else {
        this.showBusinessDropdown = false;
      }
    },

    async onSearchFocus() {
      this.showBusinessDropdown = true;
      // Load businesses if not already loaded
      if (this.availableBusinesses.length === 0) {
        await this.loadBusinesses(true);
      }
    },

    onSearchBlur() {
      setTimeout(() => {
        const searchInput = this.$refs.businessSearchInput;
        
        if (searchInput && document.activeElement !== searchInput && 
            !this.isInteractingWithDropdown && 
            !this.isLoadingMore && 
            !this.loadingBusinesses) {
          this.showBusinessDropdown = false;
        }
      }, 150);
    },

    onDropdownMouseEnter() {
      this.isInteractingWithDropdown = true;
    },

    onDropdownMouseLeave() {
      this.isInteractingWithDropdown = false;
    },

    onDropdownScroll() {
      if (this.scrollDebounce) clearTimeout(this.scrollDebounce);
      
      this.scrollDebounce = setTimeout(() => {
        const businessList = this.$refs.businessList;
        if (!businessList) return;
        
        const scrollTop = businessList.scrollTop;
        const scrollHeight = businessList.scrollHeight;
        const clientHeight = businessList.clientHeight;
        const scrollBottom = scrollTop + clientHeight;
        const threshold = 50;
        
        if (scrollBottom >= scrollHeight - threshold && 
            this.hasMoreBusinesses && 
            !this.isLoadingMore && 
            !this.loadingBusinesses) {
          this.loadMoreBusinesses();
        }
      }, 100);
    },

    toggleBusinessSelection(business) {
      const index = this.selectedBusinesses.findIndex(b => b.id === business.id);
      if (index === -1) {
        this.selectedBusinesses.push(business);
      } else {
        this.selectedBusinesses.splice(index, 1);
      }
      this.applyFilters();
    },

    removeBusinessSelection(business) {
      this.selectedBusinesses = this.selectedBusinesses.filter(b => b.id !== business.id);
      this.applyFilters();
    },

    clearAllBusinessSelections() {
      this.selectedBusinesses = [];
      this.applyFilters();
    },

    handleClickOutside(event) {
      const businessDropdown = this.$refs.businessDropdown;
      const searchInput = this.$refs.businessSearchInput;
      
      // Handle business dropdown
      if (businessDropdown && !businessDropdown.contains(event.target) && 
          searchInput && !searchInput.contains(event.target) &&
          !this.isLoadingMore && !this.loadingBusinesses) {
        this.showBusinessDropdown = false;
      }
    },

    handleKeydown(event) {
      if (!this.showBusinessDropdown) return;

      if (event.key === 'Escape') {
        this.showBusinessDropdown = false;
        
        const searchInput = this.$refs.businessSearchInput;
        if (searchInput && typeof searchInput.focus === 'function') {
          searchInput.focus();
        }
      }
    },

    onRequestUpdated() {
      this.loadRequests();
    },
    
    handleTableAction(actionData) {
      if (actionData.action === 'download' && actionData.field === 'file') {
        this.viewInvoice(actionData.row.file, actionData.row.invoice_reference);
      }
    },
    
    viewInvoice(fileUrl, invoiceReference) {
      if (!fileUrl) {
        this.$showAlert('No file available to view', 'warning');
        return;
      }
      
      try {
        this.pdfUrl = fileUrl;
        this.pdfName = `${invoiceReference}-invoice.pdf`;
        this.showPDF = true;
      } catch (error) {
        console.error('Error viewing invoice:', error);
        this.$showAlert('Failed to view invoice', 'danger');
      }
    },
    
    closePDFViewer() {
      this.showPDF = false;
      this.pdfUrl = null;
      this.pdfName = '';
    }
  }
};
</script>

<style lang="scss">
@import './FinancingRequests.scss';
</style> 